import {
  createStyles,
  Dialog,
  IconButton,
  makeStyles,
  Slide,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import React, { useState } from 'react';
// eslint-disable-next-line import/no-unresolved
import { TransitionProps } from '@material-ui/core/transitions';
import {
  BasePage,
  colors,
  ConfirmationDialog,
  dimensions,
  Footer,
} from 'design-system';
import { XCircle } from 'react-feather';

// Local
import BeneficiaryDataForm from './BeneficiaryForm';

export const CLOSE_ICON_SIZE = 24;

const useStyles = makeStyles((theme: Theme) => {
  const grey =
    'linear-gradient(359.66deg, rgba(242, 244, 243,0.4) 13.26%, rgba(255, 255, 255,0.4) 88.97%)';
  const paperBackgroundColor =
    'linear-gradient(359.66deg, rgb(242, 244, 243) 13.26%, #FFFFFF 88.97%)';
  return createStyles({
    root: {
      background: paperBackgroundColor,
    },
    container: {
      flex: 1,
      backgroundSize: '100%',
      backgroundRepeat: 'no-repeat',
      backgroundImage: `${grey}`,
      [theme.breakpoints.up('lg')]: {
        backgroundPosition: 'initial',
      },
      [theme.breakpoints.down('md')]: {
        backgroundPosition: 'bottom',
      },
      [theme.breakpoints.down('sm')]: {
        backgroundImage: 'none',
      },
    },
    mainContainer: {
      [theme.breakpoints.up('lg')]: {
        paddingRight: 10,
        paddingLeft: 10,
      },
      paddingTop: 20,
    },
    closeIcon: {
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        right: dimensions.desktopSidePadding + CLOSE_ICON_SIZE,
        top: '50%',
        marginTop: -CLOSE_ICON_SIZE / 2.0,
      },
    },
  });
});

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Slide direction="up" ref={ref} {...props} />;
});

type Props = {
  open: boolean;
  onCloseDialog: () => void;
  onSubmitBeneficiaries: () => void;
};

const BeneficiaryDialog = ({
  open,
  onCloseDialog,
  onSubmitBeneficiaries,
}: Props): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [showConfirmation, setShowConfirmation] = useState(false);

  function handleClickClose(): void {
    setShowConfirmation(true);
  }

  function handleConfirmClose(): void {
    setShowConfirmation(false);
    onCloseDialog();
  }

  function handleRejectClose(): void {
    setShowConfirmation(false);
  }

  return (
    <Dialog
      fullScreen
      open={open}
      TransitionComponent={Transition}
      classes={{
        paper: classes.root,
      }}
    >
      <div className={classes.container}>
        <BasePage
          /** We have to check if a planType exists,
           * otherwise just display the prices */
          title="Datos de beneficiarios"
          appBarPosition="sticky"
          rightActions={
            <IconButton
              id="sales-web-inspection-button-close"
              data-testid="close-button"
              className={classes.closeIcon}
              onClick={handleClickClose}
            >
              <XCircle size={CLOSE_ICON_SIZE} color={colors.white} />
            </IconButton>
          }
        >
          <BeneficiaryDataForm
            data-testid="beneficiary-form-data"
            onSubmit={onSubmitBeneficiaries}
          />
        </BasePage>
      </div>
      {/** Footer Section */}
      <Footer
        IsMobile={isMobile}
        rootStyles={{ backgroundColor: 'transparent' }}
        country="Costa Rica"
        linkText="Consulte nuestros términos y condiciones"
        linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
      />
      <ConfirmationDialog
        data-testid="confirmation-dialog"
        open={showConfirmation}
        message="Al salir perderás los cambios realizados. 
        ¿Deseas salir?"
        confirmAction={handleConfirmClose}
        rejectAction={handleRejectClose}
      />
    </Dialog>
  );
};

export default BeneficiaryDialog;
